/**
 * tm/components/LoanFpPick.tsx — 대출 점검 「팀장님」 잇기 · 바꾸기 · 새로 등록 (2026-10-18 지도사 「팀장님 입력도 여기서 가능해야겠네」)
 *  업체 머리 바로 아래 한 줄(진행 상황 링크 안에 숨어 있던 것을 밖으로) — 이름 · 번호 뒷자리로 찾기 · 없으면 그 자리에서 새 팀장님(이름 · 소속 · 휴대폰)
 *  잇으면 대출 · 업체가 그 팀장님과 이어져 팀장님 포털에도 진행이 보인다(서버 /loan-board/:id/fp)
 *  2026-10-29 지도사 「WM님이 직접 대출 요청」 — WM님도 같은 목록에서 찾아 잇는다(「박지영 WM님」 · WM 포털에 보임) · 이름표는 「소개」
 */
import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import type { FP, WM } from '../types';
import * as api from '../api';
import { C, EditIcon, MetaRow, T } from './ui';
import { typingPhone } from '../../leads/calc';   // 전화번호 하이픈 자동(2026-10-19)

const ell: CSSProperties = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 };
const inp: CSSProperties = { height: 32, border: `1.5px solid ${C.borderDk}`, borderRadius: 8, padding: '0 10px', fontFamily: 'inherit', fontSize: T.sm, minWidth: 0, background: '#fff' };
const sBtn = (on = false): CSSProperties => ({ height: 28, padding: '0 10px', borderRadius: 8, border: `1px solid ${on ? C.accent : C.border}`, background: on ? C.accent : '#fff', color: on ? '#fff' : C.text, fontSize: T.sm, fontWeight: 600, cursor: 'pointer', flexShrink: 0 });
const link: CSSProperties = { border: 'none', background: 'none', padding: '0 2px', color: C.accent, fontSize: T.sm, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' };
/** 이을 수 있는 분 — 팀장님 · WM님 한 줄 모양 */
type Person = { kind: 'fp' | 'wm'; id: string; label: string; name: string; phone: string; company: string };
const tail4 = (p?: string) => { const d = String(p || '').replace(/\D/g, ''); return d.length >= 4 ? d.slice(-4) : ''; };

export default function LoanFpPick({ current, busy, onLink, onToast }: {
  /** 지금 이어진 팀장님 표시 이름(없으면 '') */
  current: string;
  busy: boolean;
  onLink: (kind: 'fp' | 'wm', id: string) => Promise<void>;
  onToast: (m: string, k?: 'success' | 'error') => void;
}) {
  const [open, setOpen] = useState(false);
  const [fps, setFps] = useState<FP[] | null>(null);
  const [wms, setWms] = useState<WM[]>([]);
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const [co, setCo] = useState('');
  const [phone, setPhone] = useState('');
  useEffect(() => {
    if (!open || fps) return;
    api.getFps().then(r => setFps(Array.isArray(r.data) ? r.data.filter(f => f.is_active !== false) : [])).catch(() => setFps([]));
    api.getWms().then(r => setWms(Array.isArray(r.data) ? r.data : [])).catch(() => setWms([]));
  }, [open, fps]);
  const qq = q.replace(/\s/g, '');
  // 팀장님 먼저 · 이어서 WM님(같은 줄 모양 · 호칭으로 구분)
  const people = useMemo((): Person[] => (fps || []).map((f): Person => ({ kind: 'fp', id: f.id, label: `${f.name} ${f.title || '팀장'}님`, name: f.name, phone: f.phone, company: f.company || '' }))
    .concat(wms.map((w): Person => ({ kind: 'wm', id: w.id, label: `${w.name} ${w.title || 'WM'}님`, name: w.name, phone: w.phone, company: w.company || '' }))), [fps, wms]);
  const hits = useMemo(() => people.filter(p => !qq || `${p.label}${tail4(p.phone)}${p.company}`.replace(/\s/g, '').includes(qq)).slice(0, 8), [people, qq]);
  // 소속 목록 — 등록된 팀장님 소속을 많이 쓰는 순으로(표기가 갈리지 않게 · 약속 등록과 같은 방식)
  const orgs = useMemo(() => {
    const cnt = new Map<string, number>();
    (fps || []).forEach(f => { const v = (f.company || '').trim(); if (v) cnt.set(v, (cnt.get(v) || 0) + 1); });
    return Array.from(cnt.entries()).sort((a, b) => b[1] - a[1]).map(e => e[0]);
  }, [fps]);
  const close = () => { setOpen(false); setQ(''); setAdding(false); setCo(''); setPhone(''); };

  async function pick(kind: 'fp' | 'wm', id: string) { await onLink(kind, id); close(); }
  async function addNew() {
    const name = q.trim();
    if (!name) return;
    if (!co) { onToast('소속을 골라 주세요', 'error'); return; }
    if (String(phone).replace(/\D/g, '').length < 10) { onToast('휴대폰 번호를 확인해 주세요', 'error'); return; }
    try {
      const r = await api.createFp({ name, company: co, phone: phone.trim() });
      const id = r.data && r.data.id;
      if (!id) { onToast('팀장님을 등록하지 못했어요', 'error'); return; }
      setFps(null);
      await pick('fp', id);
    } catch (e) { onToast(e instanceof Error ? e.message : '팀장님을 등록하지 못했어요', 'error'); }
  }

  return (
    <div>
      {/* 머리 정보 줄(MetaRow) · 고치기는 연필 아이콘(2026-10-28 지도사 「고치기 바꾸기 버튼이 있는 것도 어색」) — 없으면 「팀장님 잇기」 글자 단추 하나만 */}
      <MetaRow label="소개" edit={current ? <EditIcon label={open ? '소개한 분 바꾸기 닫기' : '소개한 분 바꾸기'} on={open} onClick={() => (open ? close() : setOpen(true))} /> : undefined}>
        {current
          ? <b style={{ ...ell, maxWidth: 260 }}>{current}</b>
          : <button type="button" style={link} aria-expanded={open} onClick={() => (open ? close() : setOpen(true))}>{open ? '닫기' : '팀장님 · WM님 잇기'}</button>}
      </MetaRow>
      {open && (
        <div style={{ marginTop: 6, maxWidth: 560, display: 'grid', gap: 4, padding: '8px 10px', borderRadius: 8, border: `1px solid ${C.border}`, background: '#F8FAFC' }}>
          <input value={q} onChange={e => { setQ(e.target.value); setAdding(false); }} placeholder="팀장님 · WM님 이름 · 번호 뒷자리 · 소속" aria-label="이을 분 찾기" autoFocus style={inp} />
          {fps === null && <div style={{ fontSize: T.sm, color: '#6B7280', padding: '4px 2px' }}>불러오는 중…</div>}
          {fps !== null && hits.map(f => (
            <div key={`${f.kind}:${f.id}`} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 8, alignItems: 'center', minHeight: 32 }}>
              <span style={{ fontSize: T.sm, ...ell }}><b>{f.label}</b><span style={{ color: '#6B7280' }}>{[f.company, tail4(f.phone) ? `뒷자리 ${tail4(f.phone)}` : ''].filter(Boolean).map(x => `  ${x}`).join('')}</span></span>
              <button type="button" style={sBtn()} disabled={busy} onClick={() => void pick(f.kind, f.id)}>잇기</button>
            </div>
          ))}
          {fps !== null && q.trim() && !adding && (
            <button type="button" onClick={() => setAdding(true)}
              style={{ textAlign: 'left', minHeight: 32, padding: '0 8px', borderRadius: 8, border: `1px dashed ${C.border}`, background: '#fff', color: C.accent, fontSize: T.sm, fontWeight: 700, cursor: 'pointer', ...ell }}>
              「{q.trim()}」 새 팀장님으로 등록하고 잇기
            </button>
          )}
          {adding && (
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr) auto', gap: 6, alignItems: 'center' }}>
              <select value={co} onChange={e => setCo(e.target.value)} aria-label="소속" style={inp}>
                <option value="" disabled>소속 고르기</option>
                {orgs.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
              <input value={phone} onChange={e => setPhone(typingPhone(e.target.value))} placeholder="휴대폰 010-" inputMode="tel" aria-label="팀장님 휴대폰" style={inp} />
              <button type="button" style={sBtn(true)} disabled={busy} onClick={() => void addNew()}>등록하고 잇기</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
